import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { 
  FaUserShield, 
  FaTicketAlt, 
  FaUsers, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaSpinner,
  FaPlus,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaExchangeAlt,
  FaFile,
  FaLink,
  FaAlignLeft,
  FaPaperPlane,
  FaUpload
} from 'react-icons/fa';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { ticketService, scammerService, escrowService, authService } from '../services/api';
import { toast } from 'react-hot-toast';

const DashboardContainer = styled.div`
  display: grid;
  gap: 2rem;
`;

// Ajout d'une animation de rotation pour les icônes spinner
const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Style pour les icônes de chargement
const LoadingIcon = styled.span`
  .spinner {
    animation: ${spinAnimation} 1s linear infinite;
    display: inline-block;
  }
`;

// Styles pour le modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.cardDark || '#242832'};
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  
  h3 {
    font-size: 1.5rem;
    margin-top: 0;
    margin-bottom: 1.5rem;
    color: ${props => props.theme.colors.primary};
  }
  
  textarea {
    width: 100%;
    padding: 1rem;
    border-radius: 8px;
    background-color: ${props => props.theme.colors.darkBg3 || '#333'};
    border: 1px solid ${props => props.theme.colors.border || '#444'};
    color: ${props => props.theme.colors.text};
    font-family: inherit;
    font-size: 1rem;
    margin-top: 1rem;
    resize: vertical;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
  }
`;

const AdminInfo = styled.div`
  display: flex;
  align-items: center;
`;

const WelcomeText = styled.p`
  margin-right: 1rem;
  
  strong {
    color: ${props => props.theme.colors.primary};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.background};
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-weight: 500;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.background};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.background};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: ${props => props.bgColor || props.theme.colors.primary}20;
  color: ${props => props.bgColor || props.theme.colors.primary};
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const StatInfo = styled.div``;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.lightText};
  font-size: 0.9rem;
`;

const TicketsList = styled.div`
  display: grid;
  gap: 1rem;
`;

const TicketItem = styled(Card)`
  padding: 1.5rem;
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const TicketTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: ${props => props.theme.colors.text};
`;

const TicketInfo = styled.div``;

const TicketStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => {
    if (props.status === 'approved') return props.theme.colors.success + '20';
    if (props.status === 'rejected') return props.theme.colors.error + '20';
    return props.theme.colors.secondary + '20';
  }};
  color: ${props => {
    if (props.status === 'approved') return props.theme.colors.success;
    if (props.status === 'rejected') return props.theme.colors.error;
    return props.theme.colors.secondary;
  }};
  
  svg {
    margin-right: 0.25rem;
  }
`;

const TicketDetails = styled.div`
  margin-bottom: 1rem;
`;

const TicketDetailItem = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  
  strong {
    min-width: 150px;
    margin-right: 1rem;
  }
`;

const TicketDescription = styled.p`
  margin: 1rem 0;
  background-color: ${props => props.theme.colors.background};
  padding: 1rem;
  border-radius: 4px;
  color: ${props => props.theme.colors.text};
`;

const TicketActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ScammerForm = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.theme.colors.background};
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${props => props.theme.colors.primary};
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.lightText};
`;

const EscrowCard = styled.div`
  background-color: ${props => props.theme.colors.card || props.theme.colors.background};
  border-radius: 8px;
  padding: 20px;
  margin-top: 15px;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

const EscrowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  cursor: pointer;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => {
    if (props.status === 'pending') return props.theme.colors.secondary + '20';
    if (props.status === 'in_progress') return props.theme.colors.primary + '20';
    if (props.status === 'completed') return props.theme.colors.success + '20';
    if (props.status === 'rejected') return props.theme.colors.error + '20';
    return props.theme.colors.text + '20';
  }};
  color: ${props => {
    if (props.status === 'pending') return props.theme.colors.secondary;
    if (props.status === 'in_progress') return props.theme.colors.primary;
    if (props.status === 'completed') return props.theme.colors.success;
    if (props.status === 'rejected') return props.theme.colors.error;
    return props.theme.colors.text;
  }};
  margin-left: 10px;
`;

const EscrowDetailsWrapper = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 8px;
  padding: 20px;
  margin-top: 15px;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const EscrowDetailsSection = styled.div`
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${props => props.theme.colors.border || '#eee'};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const DetailItem = styled.div`
  display: flex;
  margin-bottom: 12px;
  font-size: 1rem;
`;

const DetailLabel = styled.div`
  min-width: 150px;
  font-weight: 600;
  color: ${props => props.theme.colors.text || '#333'};
`;

const DetailValue = styled.div`
  flex-grow: 1;
  color: ${props => props.theme.colors.text || '#333'};
`;

const LogsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.border || '#eee'};
  border-radius: 4px;
  background-color: ${props => props.theme.colors.background || '#f5f5f5'};
  padding: 10px;
`;

const LogItem = styled.div`
  padding: 10px;
  border-bottom: 1px solid ${props => props.theme.colors.border || '#eee'};
  color: #333;
  
  &:last-child {
    border-bottom: none;
  }
  
  strong {
    color: #000;
    display: block;
    margin-bottom: 5px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

const EscrowActions = styled.div`
  margin-top: 20px;
`;

const AdminDashboard = () => {
  const { currentAdmin, isAuthenticated, isLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('tickets');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [escrowFilter, setEscrowFilter] = useState('all');
  const [tickets, setTickets] = useState([]);
  const [scammers, setScammers] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [expandedTickets, setExpandedTickets] = useState({});
  const [expandedEscrows, setExpandedEscrows] = useState({});
  const [showAddScammerForm, setShowAddScammerForm] = useState(false);
  const [loading, setLoading] = useState({
    tickets: false,
    scammers: false,
    escrows: false,
    fileUpload: false
  });
  const [statusUpdateModal, setStatusUpdateModal] = useState({
    isOpen: false,
    ticketId: null,
    status: '',
  });
  const [escrowActionModal, setEscrowActionModal] = useState({
    isOpen: false,
    escrowId: null,
    action: '',
    reason: '',
    logMessage: ''
  });
  const [fileUploadModal, setFileUploadModal] = useState({
    isOpen: false,
    escrowId: null,
    contentType: 'file'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState('');
  const [reason, setReason] = useState('');
  const [logMessage, setLogMessage] = useState('');
  const [scammerForm, setScammerForm] = useState({
    telegramUsername: '',
    reason: '',
    reportedBy: ''
  });
  const [stats, setStats] = useState({
    totalTickets: 0,
    pendingTickets: 0,
    totalScammers: 0
  });

  // État du profil administrateur pour l'onglet Profil
  const [walletAddress, setWalletAddress] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ text: '', type: '' });

  // Ajoutons un état pour stocker l'adresse temporaire
  const [tempWalletAddress, setTempWalletAddress] = useState('');

  // Ajoutons l'état pour le message du fichier à envoyer à l'acheteur
  const [tempFileMessage, setTempFileMessage] = useState('');

  // Initialiser l'adresse de portefeuille quand l'admin change
  useEffect(() => {
    if (currentAdmin && currentAdmin.walletAddress) {
      setWalletAddress(currentAdmin.walletAddress);
    }
  }, [currentAdmin]);

  // Fonction de mise à jour du profil
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage({ text: '', type: '' });

    try {
      await authService.updateProfile({ walletAddress });
      
      // Update local admin state
      if (currentAdmin) {
        currentAdmin.walletAddress = walletAddress;
      }
      
      setProfileMessage({ 
        text: 'Profil mis à jour avec succès', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setProfileMessage({ 
        text: `Erreur: ${error.response?.data?.message || error.message}`, 
        type: 'error' 
      });
    } finally {
      setProfileLoading(false);
    }
  };

  // Fetch tickets and scammers on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchTickets();
      fetchScammers();
    }
  }, [isAuthenticated]);

  // Fetch tickets filtered by status
  const fetchTickets = async (status = 'all') => {
    try {
      setLoading(prev => ({ ...prev, tickets: true }));
      
      // Get tickets based on status
      const response = status === 'all' 
        ? await ticketService.getAll()
        : await ticketService.getByStatus(status);
      
      // Set tickets in state
      setTickets(response.data);
      
      // Calculate stats
      const pendingCount = status === 'pending' 
        ? response.data.length 
        : response.data.filter(ticket => ticket.status === 'pending').length;
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalTickets: status === 'all' ? response.data.length : prevStats.totalTickets,
        pendingTickets: pendingCount
      }));
      
      // Update filter state
      setTicketFilter(status);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      alert('Failed to load tickets. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, tickets: false }));
    }
  };

  const fetchScammers = async () => {
    try {
      setLoading(prev => ({ ...prev, scammers: true }));
      const scammersData = await scammerService.getAll();
      setScammers(scammersData);
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        totalScammers: scammersData.length
      }));
    } catch (err) {
      console.error('Failed to fetch scammers:', err);
    } finally {
      setLoading(prev => ({ ...prev, scammers: false }));
    }
  };

  // Function to fetch escrows
  const fetchEscrows = async (status = 'all') => {
    setLoading(prev => ({ ...prev, escrows: true }));
    try {
      const data = await escrowService.getAll(status);
      setEscrows(data);
    } catch (error) {
      console.error('Error fetching escrows:', error);
    } finally {
      setLoading(prev => ({ ...prev, escrows: false }));
    }
  };

  // Add new useEffect for escrows
  useEffect(() => {
    if (activeTab === 'escrows') {
      fetchEscrows(escrowFilter);
    }
  }, [activeTab, escrowFilter]);

  // Ouvrir le modal de mise à jour du statut
  const openStatusUpdateModal = (ticketId, status) => {
    setStatusUpdateModal({
      isOpen: true,
      ticketId,
      status,
      reason: ''
    });
  };

  // Fermer le modal de mise à jour du statut
  const closeStatusUpdateModal = () => {
    setStatusUpdateModal({
      isOpen: false,
      ticketId: null,
      status: null,
      reason: ''
    });
  };

  // Gérer le changement de raison dans le modal
  const handleReasonChange = (e) => {
    setStatusUpdateModal(prev => ({
      ...prev,
      reason: e.target.value
    }));
  };

  // Soumettre la mise à jour du statut
  const submitStatusUpdate = async () => {
    const { ticketId, status, reason } = statusUpdateModal;
    
    if ((status === 'approved' || status === 'rejected') && !reason.trim()) {
      alert('Please provide a reason.');
      return;
    }
    
    try {
      await ticketService.updateStatus(ticketId, status, reason);
      
      // Update local state
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket._id === ticketId ? { ...ticket, status, rejectionReason: status === 'rejected' ? reason : undefined } : ticket
        )
      );
      
      // Refresh stats and scammers list if a ticket is approved
      if (status === 'approved') {
        fetchScammers();
        
        // Update pending tickets count
        setStats(prevStats => ({
          ...prevStats,
          pendingTickets: Math.max(0, prevStats.pendingTickets - 1)
        }));
      }
      
      closeStatusUpdateModal();
    } catch (err) {
      console.error('Failed to update ticket status:', err);
      alert('Failed to update ticket status. Please try again.');
    }
  };

  // Supprimer un ticket
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }
    
    try {
      await ticketService.delete(ticketId);
      
      // Update local state
      setTickets(prevTickets => 
        prevTickets.filter(ticket => ticket._id !== ticketId)
      );
      
      // Update stats if the deleted ticket was pending
      const deletedTicket = tickets.find(ticket => ticket._id === ticketId);
      if (deletedTicket && deletedTicket.status === 'pending') {
        setStats(prevStats => ({
          ...prevStats,
          pendingTickets: Math.max(0, prevStats.pendingTickets - 1),
          totalTickets: Math.max(0, prevStats.totalTickets - 1)
        }));
      } else {
        setStats(prevStats => ({
          ...prevStats,
          totalTickets: Math.max(0, prevStats.totalTickets - 1)
        }));
      }
    } catch (err) {
      console.error('Failed to delete ticket:', err);
      alert('Failed to delete ticket. Please try again.');
    }
  };

  const handleAddScammer = async (e) => {
    e.preventDefault();
    
    try {
      // Préparer les données à envoyer selon le format attendu par le backend
      const scammerData = {
        telegramUsername: scammerForm.telegramUsername,
        reason: scammerForm.reason,
        reportedBy: scammerForm.reportedBy,
        // On peut ajouter une note sur les preuves dans la raison si l'utilisateur a fourni une description
        ...(scammerForm.evidenceDescription && {
          reason: `${scammerForm.reason}\n\nEvidence: ${scammerForm.evidenceDescription}`
        })
      };
      
      await scammerService.create(scammerData);
      
      // Reset form and refresh scammers list
      setScammerForm({
        telegramUsername: '',
        reason: '',
        reportedBy: ''
      });
      fetchScammers();
    } catch (err) {
      console.error('Failed to add scammer:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setScammerForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleTicketDetails = (ticketId) => {
    setExpandedTickets(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Escrow action modals
  const openEscrowActionModal = (escrowId, action) => {
    setEscrowActionModal({
      isOpen: true,
      escrowId,
      action,
      reason: '',
      logMessage: ''
    });
    
    // Si l'action est assign ou complete, afficher la crypto-monnaie comme rappel pour l'admin
    if (action === 'assign' || action === 'complete') {
      // Trouver l'escrow correspondant
      const currentEscrow = escrows.find(e => e._id === escrowId);
      if (currentEscrow && currentEscrow.cryptoCurrency && currentEscrow.cryptoCurrency !== 'Non spécifiée') {
        toast.success(`Rappel: L'acheteur souhaite utiliser ${currentEscrow.cryptoCurrency} pour cette transaction.`, {
          duration: 6000
        });
      }
    }
  };

  const closeEscrowActionModal = () => {
    setEscrowActionModal({
      isOpen: false,
      escrowId: null,
      action: '',
      reason: '',
      logMessage: ''
    });
    setReason('');
    setLogMessage('');
    setTempWalletAddress('');
    setTempFileMessage('');
  };

  const handleEscrowReasonChange = (e) => {
    setReason(e.target.value);
  };

  const handleLogMessageChange = (e) => {
    setLogMessage(e.target.value);
  };

  const submitEscrowAction = async () => {
    const { escrowId, action } = escrowActionModal;

    if (!escrowId) return;

    try {
      let result;
      switch (action) {
        case 'assign':
          // Utiliser l'adresse temporaire qui est obligatoire
          if (!tempWalletAddress.trim()) {
            toast.error('Veuillez fournir une adresse de portefeuille');
            return;
          }
          
          const walletAddressData = { walletAddress: tempWalletAddress.trim() };
          console.log('Envoi de l\'adresse pour assignation:', walletAddressData);
          
          result = await escrowService.assignEscrow(escrowId, walletAddressData);
          
          console.log('Résultat de l\'assignation:', result);
          
          // Ajouter un log pour confirmer l'assignation avec l'adresse
          await escrowService.addLog(escrowId, `Escrow assigné avec adresse: ${tempWalletAddress.trim()}`);
          
          toast.success('Escrow pris en charge avec succès');
          toast.success('L\'adresse a été envoyée à l\'acheteur via Telegram');
          break;
        case 'complete':
          result = await escrowService.completeEscrow(escrowId);
          toast.success('Escrow complété avec succès');
          break;
        case 'reject':
          if (!reason) {
            toast.error('Veuillez fournir une raison pour le rejet');
            return;
          }
          result = await escrowService.rejectEscrow(escrowId, reason);
          toast.success('Escrow rejeté avec succès');
          break;
        case 'log':
          if (!logMessage) {
            toast.error('Veuillez fournir un message pour le log');
            return;
          }
          result = await escrowService.addLog(escrowId, logMessage);
          toast.success('Log ajouté avec succès');
          break;
        case 'sendFile':
          if (!tempFileMessage.trim()) {
            toast.error('Veuillez fournir un message avec le lien ou les instructions');
            return;
          }
          result = await escrowService.sendContentToBuyer(escrowId, tempFileMessage);
          toast.success('Message envoyé à l\'acheteur avec succès');
          break;
        default:
          break;
      }

      console.log(`Action ${action} effectuée avec succès:`, result);

      // Réinitialiser les champs temporaires
      setTempWalletAddress('');
      setTempFileMessage('');

      // Refresh escrows after action
      fetchEscrows(escrowFilter);
      closeEscrowActionModal();
    } catch (error) {
      console.error(`Error performing ${action} on escrow ${escrowId}:`, error);
      const errorMessage = error.response?.data?.message || error.message || 'Une erreur est survenue';
      toast.error(`Erreur: ${errorMessage}`);
    }
  };

  // Toggle escrow details visibility
  const toggleEscrowDetails = (escrowId) => {
    setExpandedEscrows(prev => ({
      ...prev,
      [escrowId]: !prev[escrowId]
    }));
  };

  // Pour gérer le changement de l'adresse temporaire
  const handleTempWalletChange = (e) => {
    setTempWalletAddress(e.target.value);
  };

  // Handler pour le message de fichier
  const handleTempFileMessageChange = (e) => {
    setTempFileMessage(e.target.value);
  };

  // Fonction pour gérer le changement de fichier sélectionné
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Fonction pour gérer le changement de contenu (lien ou texte)
  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  // Fonction pour changer le type de contenu
  const handleContentTypeChange = (type) => {
    setFileUploadModal(prev => ({
      ...prev,
      contentType: type
    }));
    // Réinitialiser le contenu quand on change de type
    setSelectedFile(null);
    setContent('');
  };

  // Fonction pour ouvrir le modal d'upload de fichier
  const openFileUploadModal = (escrowId) => {
    setFileUploadModal({
      isOpen: true,
      escrowId,
      contentType: 'file'
    });
    setSelectedFile(null);
    setContent('');
  };

  // Fonction pour fermer le modal d'upload de fichier
  const closeFileUploadModal = () => {
    setFileUploadModal({
      isOpen: false,
      escrowId: null,
      contentType: 'file'
    });
    setSelectedFile(null);
    setContent('');
  };

  // Fonction pour soumettre le contenu (fichier, lien ou texte)
  const submitContent = async () => {
    const { escrowId, contentType } = fileUploadModal;
    
    if (!escrowId) return;
    
    try {
      setLoading(prev => ({ ...prev, fileUpload: true }));
      
      let result;
      
      switch (contentType) {
        case 'file':
          if (!selectedFile) {
            toast.error('Veuillez sélectionner un fichier');
            return;
          }
          
          // Upload du fichier
          result = await escrowService.uploadFile(escrowId, selectedFile);
          toast.success('Fichier uploadé avec succès');
          break;
          
        case 'link':
          if (!content.trim()) {
            toast.error('Veuillez entrer un lien');
            return;
          }
          
          // Validation basique de lien
          if (!content.startsWith('http://') && !content.startsWith('https://')) {
            toast.error('Veuillez entrer un lien valide commençant par http:// ou https://');
            return;
          }
          
          // Enregistrer le lien externe
          result = await escrowService.uploadTextOrLink(escrowId, content, true);
          toast.success('Lien enregistré avec succès');
          break;
          
        case 'text':
          if (!content.trim()) {
            toast.error('Veuillez entrer du texte');
            return;
          }
          
          // Enregistrer le contenu textuel
          result = await escrowService.uploadTextOrLink(escrowId, content, false);
          toast.success('Texte enregistré avec succès');
          break;
          
        default:
          break;
      }
      
      console.log(`Contenu (${contentType}) ajouté avec succès:`, result);
      
      // Rafraîchir les escrows
      fetchEscrows(escrowFilter);
      closeFileUploadModal();
    } catch (error) {
      console.error(`Erreur lors de l'ajout de contenu (${contentType}):`, error);
      const errorMessage = error.response?.data?.message || error.message || 'Une erreur est survenue';
      toast.error(`Erreur: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, fileUpload: false }));
    }
  };

  // Fonction pour ouvrir le modal d'envoi de fichier à l'acheteur
  const openSendFileToBuyerModal = (escrowId) => {
    // Trouvons l'escrow concerné
    const escrow = escrows.find(e => e._id === escrowId);
    
    // Préparons un message pré-rempli si un fichier ou un lien est déjà attaché
    let defaultMessage = '';
    if (escrow.fileUrl) {
      defaultMessage = `Fichier vérifié et disponible au téléchargement : ${process.env.REACT_APP_API_URL || ''}${escrow.fileUrl}`;
    } else if (escrow.externalFileUrl) {
      defaultMessage = `Fichier vérifié et disponible à ce lien externe : ${escrow.externalFileUrl}`;
    } else if (escrow.textContent) {
      defaultMessage = `Contenu vérifié : ${escrow.textContent}`;
    }
    
    setTempFileMessage(defaultMessage);
    
    setEscrowActionModal({
      isOpen: true,
      escrowId,
      action: 'sendFile',
      reason: '',
      logMessage: ''
    });
  };

  // Améliorons la fonction de formatage des dates
  const formatDateSafely = (dateString) => {
    if (!dateString) return 'Date non disponible';
    
    try {
      // Si la date est au format timestamp (nombre)
      if (!isNaN(Number(dateString))) {
        return new Date(Number(dateString)).toLocaleString();
      }
      
      // Si c'est une chaîne ISO ou autre format reconnu
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Format de date invalide';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Erreur de formatage de date:', error, dateString);
      return 'Erreur de date';
    }
  };

  // Section de rendu des détails d'escrow
  const renderEscrowDetails = (escrow) => {
    const isExpanded = expandedEscrows[escrow._id];
    const isAssigned = escrow.assignedAdmin && escrow.assignedAdmin._id;
    // eslint-disable-next-line no-unused-vars
    const isPending = escrow.status === 'pending';
    const isComplete = escrow.status === 'completed';
    const isRejected = escrow.status === 'rejected';
    const isInProgress = escrow.status === 'in_progress';
    const hasFile = escrow.fileUrl || escrow.externalFileUrl || escrow.textContent;
    const serviceFeePercentage = 10; // 10% de frais
    const escrowAmount = parseFloat(escrow.amount);
    const serviceFee = !isNaN(escrowAmount) ? (escrowAmount * serviceFeePercentage / 100).toFixed(2) : 'N/A';

    return (
      <EscrowCard key={escrow._id} $isExpanded={isExpanded}>
        <EscrowHeader onClick={() => toggleEscrowDetails(escrow._id)}>
          <div>
            <strong>{escrow.description}</strong>
            <StatusBadge status={escrow.status}>
              {escrow.status.charAt(0).toUpperCase() + escrow.status.slice(1)}
            </StatusBadge>
          </div>
          <div>
            <strong>{escrow.amount}</strong> <small>{escrow.cryptoCurrency}</small>
          </div>
        </EscrowHeader>

        {isExpanded && (
          <EscrowDetailsWrapper>
            <EscrowDetailsSection>
              <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Détails de l'escrow</h4>
              
              <DetailItem>
                <DetailLabel>ID:</DetailLabel>
                <DetailValue>{escrow._id}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Montant:</DetailLabel>
                <DetailValue>{escrow.amount} {escrow.cryptoCurrency}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Frais de service (10%):</DetailLabel>
                <DetailValue>{serviceFee}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Description:</DetailLabel>
                <DetailValue>{escrow.description}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Statut:</DetailLabel>
                <DetailValue>
                  <StatusBadge status={escrow.status}>
                    {escrow.status.charAt(0).toUpperCase() + escrow.status.slice(1)}
                  </StatusBadge>
                </DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Créé le:</DetailLabel>
                <DetailValue>{formatDateSafely(escrow.createdAt)}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Acheteur:</DetailLabel>
                <DetailValue>@{escrow.buyer?.telegramUsername || escrow.buyerTelegramUsername || 'Inconnu'}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Vendeur:</DetailLabel>
                <DetailValue>@{escrow.seller?.telegramUsername || escrow.sellerTelegramUsername || 'Inconnu'}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>Admin assigné:</DetailLabel>
                <DetailValue>
                  {escrow.assignedAdmin 
                    ? `@${escrow.assignedAdmin.username}` 
                    : <span style={{color: '#ff9800'}}>Non assigné</span>}
                </DetailValue>
              </DetailItem>

              {escrow.walletAddress && (
                <DetailItem>
                  <DetailLabel>Adresse de paiement:</DetailLabel>
                  <DetailValue 
                    style={{ 
                      wordBreak: 'break-all', 
                      cursor: 'pointer', 
                      backgroundColor: 'rgba(0,0,0,0.05)', 
                      padding: '6px 10px', 
                      borderRadius: '4px',
                      border: '1px dashed #ccc'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(escrow.walletAddress)
                        .then(() => {
                          toast.success('Adresse copiée dans le presse-papier!');
                        })
                        .catch(err => {
                          console.error('Erreur lors de la copie:', err);
                          toast.error('Impossible de copier l\'adresse.');
                        });
                    }}
                    title="Cliquer pour copier l'adresse"
                  >
                    {escrow.walletAddress}
                  </DetailValue>
                </DetailItem>
              )}
            </EscrowDetailsSection>

            {escrow.fileUrl && (
              <EscrowDetailsSection>
                <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Fichier</h4>
                <Button 
                  as="a" 
                  href={`${process.env.REACT_APP_API_URL || ''}${escrow.fileUrl}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ marginBottom: '10px', display: 'inline-block' }}
                >
                  Télécharger le fichier
                </Button>
                <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#888' }}>
                  Note: Les fichiers sont supprimés 24h après que l'escrow soit complété
                </div>
              </EscrowDetailsSection>
            )}

            {escrow.externalFileUrl && (
              <EscrowDetailsSection>
                <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Lien externe</h4>
                <Button 
                  as="a" 
                  href={escrow.externalFileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ marginBottom: '10px', display: 'inline-block' }}
                >
                  Accéder au lien externe
                </Button>
                <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#888' }}>
                  Attention: Il s'agit d'un lien externe, vérifiez qu'il est sécurisé avant d'y accéder
                </div>
              </EscrowDetailsSection>
            )}

            {escrow.textContent && (
              <EscrowDetailsSection>
                <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Contenu textuel</h4>
                <div style={{ color: '#888', fontStyle: 'italic' }}>{escrow.textContent}</div>
              </EscrowDetailsSection>
            )}

            {escrow.logs && escrow.logs.length > 0 && (
              <EscrowDetailsSection>
                <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Logs</h4>
                <LogsList>
                  {escrow.logs.map((log, index) => (
                    <LogItem key={index}>
                      <strong>{formatDateSafely(log.timestamp)}</strong>
                      <div>{log.message}</div>
                    </LogItem>
                  ))}
                </LogsList>
              </EscrowDetailsSection>
            )}

            {(!escrow.logs || escrow.logs.length === 0) && (
              <EscrowDetailsSection>
                <h4 style={{ marginBottom: '15px', color: 'inherit' }}>Logs</h4>
                <div style={{ color: '#888', fontStyle: 'italic' }}>Aucun log disponible pour cet escrow</div>
              </EscrowDetailsSection>
            )}

            <EscrowActions>
              <ActionButtons>
                {!isAssigned && (
                  <Button 
                    onClick={() => openEscrowActionModal(escrow._id, 'assign')}
                    disabled={loading.escrowAction}
                    variant="primary"
                  >
                    {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : 'Prendre en charge'}
                  </Button>
                )}
                
                {!isComplete && !isRejected && (
                  <Button 
                    onClick={() => openEscrowActionModal(escrow._id, 'complete')}
                    disabled={loading.escrowAction}
                    variant="success"
                  >
                    {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : 'Compléter'}
                  </Button>
                )}
                
                {!isComplete && !isRejected && (
                  <Button 
                    onClick={() => openEscrowActionModal(escrow._id, 'reject')}
                    disabled={loading.escrowAction}
                    variant="danger"
                  >
                    {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : 'Rejeter'}
                  </Button>
                )}
                
                <Button 
                  onClick={() => openEscrowActionModal(escrow._id, 'log')}
                  disabled={loading.escrowAction}
                  variant="outline"
                >
                  {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : 'Ajouter log'}
                </Button>

                {isInProgress && (
                  <Button 
                    onClick={() => openFileUploadModal(escrow._id)}
                    disabled={loading.escrowAction}
                    variant="outline"
                    style={{ backgroundColor: '#4CAF50', color: 'white' }}
                  >
                    {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : <><FaUpload style={{ marginRight: '0.5rem' }} /> Envoie un lien/du texte</>}
                  </Button>
                )}

                {isInProgress && hasFile && (
                  <Button 
                    onClick={() => openSendFileToBuyerModal(escrow._id)}
                    disabled={loading.escrowAction}
                    variant="info"
                    style={{ backgroundColor: '#2196F3' }}
                  >
                    {loading.escrowAction ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : <><FaPaperPlane style={{ marginRight: '0.5rem' }} /> Envoyer à l'acheteur</>}
                  </Button>
                )}
              </ActionButtons>
            </EscrowActions>
          </EscrowDetailsWrapper>
        )}
      </EscrowCard>
    );
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/admin/login" replace />;
  }

  if (isLoading) {
    return (
      <LoadingSpinner>
        <LoadingIcon>
          <FaSpinner className="spinner" />
        </LoadingIcon>
        Chargement du dashboard...
      </LoadingSpinner>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>
          <FaUserShield />
          Dashboard Administrateur
        </Title>
        <AdminInfo>
          <WelcomeText>
            Bienvenue, <strong>{currentAdmin?.username || currentAdmin?.name || 'Admin'}</strong>
          </WelcomeText>
          <Button onClick={logout}>
            Déconnexion
          </Button>
        </AdminInfo>
      </Header>
      
      <StatsContainer>
        <StatCard>
          <StatIcon bgColor={stats.pendingTickets > 0 ? '#e74c3c' : undefined}>
            <FaTicketAlt />
          </StatIcon>
          <StatInfo>
            <StatValue>{stats.pendingTickets}</StatValue>
            <StatLabel>Tickets en attente</StatLabel>
          </StatInfo>
        </StatCard>
        
        <StatCard>
          <StatIcon>
            <FaTicketAlt />
          </StatIcon>
          <StatInfo>
            <StatValue>{stats.totalTickets}</StatValue>
            <StatLabel>Total des tickets</StatLabel>
          </StatInfo>
        </StatCard>
        
        <StatCard>
          <StatIcon bgColor="#4a90e2">
            <FaUsers />
          </StatIcon>
          <StatInfo>
            <StatValue>{stats.totalScammers}</StatValue>
            <StatLabel>Scammers listés</StatLabel>
          </StatInfo>
        </StatCard>
      </StatsContainer>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'tickets'} 
          onClick={() => setActiveTab('tickets')}
        >
          <FaTicketAlt />
          Tickets
        </Tab>
        <Tab 
          active={activeTab === 'scammers'} 
          onClick={() => setActiveTab('scammers')}
        >
          <FaUsers />
          Gestion des scammers
        </Tab>
        <Tab 
          active={activeTab === 'escrows'} 
          onClick={() => setActiveTab('escrows')}
        >
          <FaExchangeAlt />
          Escrows
        </Tab>
        <Tab
          active={activeTab === 'profile'}
          onClick={() => setActiveTab('profile')}
        >
          <FaUserShield />
          Profil
        </Tab>
      </TabsContainer>
      
      {activeTab === 'tickets' ? (
        <div>
          <Header>
            <Title style={{ fontSize: '1.5rem' }}>Gestion des tickets</Title>
          </Header>
          
          <FilterBar>
            <FilterLabel>Filtrer par statut :</FilterLabel>
            <FilterButton 
              active={ticketFilter === 'all'}
              onClick={() => fetchTickets('all')}
            >
              Tous
            </FilterButton>
            <FilterButton 
              active={ticketFilter === 'pending'}
              onClick={() => fetchTickets('pending')}
            >
              En attente
            </FilterButton>
            <FilterButton 
              active={ticketFilter === 'approved'}
              onClick={() => fetchTickets('approved')}
            >
              Approuvés
            </FilterButton>
            <FilterButton 
              active={ticketFilter === 'rejected'}
              onClick={() => fetchTickets('rejected')}
            >
              Rejetés
            </FilterButton>
          </FilterBar>
          
          {loading.tickets ? (
            <LoadingSpinner>
              <LoadingIcon>
                <FaSpinner className="spinner" />
              </LoadingIcon> 
              Chargement des tickets...
            </LoadingSpinner>
          ) : tickets.length > 0 ? (
            <TicketsList>
              {tickets.map(ticket => (
                <TicketItem key={ticket._id}>
                  <TicketHeader>
                    <TicketInfo>
                      <TicketTitle>{ticket.scammerTelegramUsername}</TicketTitle>
                      <small>Signalé le {formatDate(ticket.createdAt)}</small>
                    </TicketInfo>
                    <div>
                      {ticket.status === 'pending' && '⏳ En attente'}
                      {ticket.status === 'approved' && (
                        <TicketStatus status="approved">
                          <FaCheckCircle />
                          Approuvé
                        </TicketStatus>
                      )}
                      {ticket.status === 'rejected' && (
                        <TicketStatus status="rejected">
                          <FaTimesCircle />
                          Rejeté
                        </TicketStatus>
                      )}
                    </div>
                  </TicketHeader>
                  
                  <Button 
                    variant="outline"
                    small
                    onClick={() => toggleTicketDetails(ticket._id)}
                  >
                    {expandedTickets[ticket._id] ? (
                      <>
                        <FaEyeSlash /> Masquer les détails
                      </>
                    ) : (
                      <>
                        <FaEye /> Voir les détails
                      </>
                    )}
                  </Button>
                  
                  {expandedTickets[ticket._id] && (
                    <TicketDetails>
                      <TicketDescription>{ticket.reason}</TicketDescription>
                      
                      <TicketDetailItem>
                        <strong>Scammer signalé:</strong> {ticket.scammerTelegramUsername}
                      </TicketDetailItem>
                      
                      <TicketDetailItem>
                        <strong>Signalé par:</strong> {ticket.telegramUsername}
                      </TicketDetailItem>
                      
                      {ticket.rejectionReason && (
                        <TicketDetailItem>
                          <strong>Raison du rejet:</strong> {ticket.rejectionReason}
                        </TicketDetailItem>
                      )}
                      
                      {ticket.evidenceImages && ticket.evidenceImages.length > 0 && (
                        <TicketDetailItem>
                          <strong>Images de preuve:</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                            {ticket.evidenceImages.map((image, index) => (
                              <a 
                                key={index}
                                href={`${process.env.REACT_APP_API_URL}/tickets/images/${image.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={`${process.env.REACT_APP_API_URL}/tickets/images/${image.filename}`}
                                  alt={`Preuve ${index + 1}`}
                                  style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                              </a>
                            ))}
                          </div>
                        </TicketDetailItem>
                      )}
                      
                      {ticket.status === 'pending' && (
                        <TicketActions>
                          <Button 
                            variant="success"
                            small
                            onClick={() => openStatusUpdateModal(ticket._id, 'approved')}
                          >
                            <FaCheckCircle /> Approuver
                          </Button>
                          <Button 
                            variant="danger"
                            small
                            onClick={() => openStatusUpdateModal(ticket._id, 'rejected')}
                          >
                            <FaTimesCircle /> Rejeter
                          </Button>
                          <Button 
                            variant="outline"
                            small
                            onClick={() => handleDeleteTicket(ticket._id)}
                          >
                            <FaExclamationTriangle /> Supprimer
                          </Button>
                        </TicketActions>
                      )}
                    </TicketDetails>
                  )}
                </TicketItem>
              ))}
            </TicketsList>
          ) : (
            <NoResults>Aucun ticket trouvé correspondant au filtre actuel.</NoResults>
          )}
          
          {statusUpdateModal.isOpen && (
            <ModalOverlay>
              <ModalContent>
                <h3>{statusUpdateModal.status === 'approved' ? 'Approuver le ticket' : 'Rejeter le ticket'}</h3>
                
                <TicketDescription>
                  {statusUpdateModal.status === 'approved'
                    ? 'Veuillez fournir une raison pour laquelle vous approuvez ce signalement (optionnel).'
                    : 'Veuillez fournir une raison pour laquelle vous rejetez ce signalement.'}
                </TicketDescription>
                
                <textarea
                  rows="4"
                  value={statusUpdateModal.reason}
                  onChange={handleReasonChange}
                  placeholder={statusUpdateModal.status === 'approved'
                    ? 'Raison optionnelle pour l\'approbation...'
                    : 'Expliquez pourquoi ce signalement est rejeté...'}
                />
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <Button 
                    variant="outline" 
                    onClick={closeStatusUpdateModal}
                  >
                    Annuler
                  </Button>
                  <Button 
                    variant={statusUpdateModal.status === 'approved' ? 'success' : 'danger'}
                    onClick={submitStatusUpdate}
                  >
                    {statusUpdateModal.status === 'approved' ? 'Approuver' : 'Rejeter'}
                  </Button>
                </div>
              </ModalContent>
            </ModalOverlay>
          )}
        </div>
      ) : activeTab === 'scammers' ? (
        <div>
          <Header>
            <Title style={{ fontSize: '1.5rem' }}>Gestion des scammers</Title>
            {!showAddScammerForm && (
              <Button onClick={() => setShowAddScammerForm(true)}>
                <FaPlus />
                Ajouter un scammer
              </Button>
            )}
          </Header>
          
          {showAddScammerForm && (
            <Card>
              <h3>Ajouter un nouveau scammer</h3>
              <ScammerForm onSubmit={handleAddScammer}>
                <FormGroup>
                  <Label htmlFor="telegramUsername">Nom d'utilisateur Telegram</Label>
                  <Input 
                    type="text" 
                    id="telegramUsername" 
                    name="telegramUsername" 
                    value={scammerForm.telegramUsername}
                    onChange={handleInputChange}
                    required
                    placeholder="@username"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="reason">Raison</Label>
                  <textarea 
                    id="reason" 
                    name="reason" 
                    value={scammerForm.reason}
                    onChange={handleInputChange}
                    required
                    placeholder="Décrivez pourquoi cette personne est ajoutée à la liste des scammers..."
                    rows="4"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="evidenceDescription">Description des preuves (optionnel)</Label>
                  <textarea 
                    id="evidenceDescription" 
                    name="evidenceDescription" 
                    value={scammerForm.evidenceDescription}
                    onChange={handleInputChange}
                    placeholder="Décrivez les preuves disponibles..."
                    rows="3"
                  />
                </FormGroup>
                
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowAddScammerForm(false)}
                  >
                    Annuler
                  </Button>
                  <Button type="submit">Ajouter</Button>
                </div>
              </ScammerForm>
            </Card>
          )}
          
          {loading.scammers ? (
            <LoadingSpinner><LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> Chargement des scammers...</LoadingSpinner>
          ) : scammers.length > 0 ? (
            <TicketsList>
              {scammers.map(scammer => (
                <TicketItem key={scammer._id}>
                  <TicketHeader>
                    <TicketInfo>
                      <TicketTitle>{scammer.telegramUsername}</TicketTitle>
                      <small>Ajouté le {formatDate(scammer.createdAt)}</small>
                    </TicketInfo>
                    <div>
                      <TicketStatus status="approved">
                        <FaUsers />
                        Scammer confirmé
                      </TicketStatus>
                    </div>
                  </TicketHeader>
                  
                  <TicketDescription>{scammer.reason}</TicketDescription>
                  
                  <TicketDetailItem>
                    <strong>Signalé par:</strong> {scammer.reportedBy}
                  </TicketDetailItem>
                </TicketItem>
              ))}
            </TicketsList>
          ) : (
            <NoResults>Aucun scammer trouvé dans la base de données.</NoResults>
          )}
        </div>
      ) : activeTab === 'escrows' ? (
        <>
          <FilterBar>
            <FilterLabel>Filtrer par statut:</FilterLabel>
            <FilterButton
              active={escrowFilter === 'all'}
              onClick={() => setEscrowFilter('all')}
            >
              Tous
            </FilterButton>
            <FilterButton
              active={escrowFilter === 'pending'}
              onClick={() => setEscrowFilter('pending')}
            >
              En attente
            </FilterButton>
            <FilterButton
              active={escrowFilter === 'in_progress'}
              onClick={() => setEscrowFilter('in_progress')}
            >
              En cours
            </FilterButton>
            <FilterButton
              active={escrowFilter === 'completed'}
              onClick={() => setEscrowFilter('completed')}
            >
              Complétés
            </FilterButton>
            <FilterButton
              active={escrowFilter === 'rejected'}
              onClick={() => setEscrowFilter('rejected')}
            >
              Rejetés
            </FilterButton>
          </FilterBar>

          <StatsContainer>
            <StatCard>
              <StatIcon bgColor="#FEF3C7">
                <FaSpinner />
              </StatIcon>
              <StatInfo>
                <StatValue>
                  {escrows.filter(escrow => escrow.status === 'pending').length}
                </StatValue>
                <StatLabel>En attente</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon bgColor="#DBEAFE">
                <FaExchangeAlt />
              </StatIcon>
              <StatInfo>
                <StatValue>
                  {escrows.filter(escrow => escrow.status === 'in_progress').length}
                </StatValue>
                <StatLabel>En cours</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon bgColor="#D1FAE5">
                <FaCheckCircle />
              </StatIcon>
              <StatInfo>
                <StatValue>
                  {escrows.filter(escrow => escrow.status === 'completed').length}
                </StatValue>
                <StatLabel>Complétés</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon bgColor="#FEE2E2">
                <FaTimesCircle />
              </StatIcon>
              <StatInfo>
                <StatValue>
                  {escrows.filter(escrow => escrow.status === 'rejected').length}
                </StatValue>
                <StatLabel>Rejetés</StatLabel>
              </StatInfo>
            </StatCard>
          </StatsContainer>

          {loading.escrows ? (
            <div>Chargement des escrows...</div>
          ) : escrows.length === 0 ? (
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                Aucun escrow trouvé avec ce statut.
              </div>
            </Card>
          ) : (
            <TicketsList>
              {escrows.map(escrow => renderEscrowDetails(escrow))}
            </TicketsList>
          )}
        </>
      ) : activeTab === 'profile' && (
        <div>
          <Card>
            <div style={{ padding: '2rem' }}>
              <h2 style={{ marginTop: 0 }}>Profil Administrateur</h2>
              
              <form onSubmit={handleProfileUpdate}>
                <FormGroup>
                  <Label htmlFor="username">Nom d'utilisateur</Label>
                  <Input
                    type="text"
                    id="username"
                    value={currentAdmin?.username || ''}
                    disabled
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="name">Nom complet</Label>
                  <Input
                    type="text"
                    id="name"
                    value={currentAdmin?.name || ''}
                    disabled
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="role">Rôle</Label>
                  <Input
                    type="text"
                    id="role"
                    value={currentAdmin?.role || 'holystaff'}
                    disabled
                  />
                </FormGroup>
                
                {profileMessage.text && (
                  <div style={{ 
                    padding: '0.75rem', 
                    marginTop: '1rem', 
                    borderRadius: '4px',
                    backgroundColor: profileMessage.type === 'success' ? '#D1FAE5' : '#FEE2E2',
                    color: profileMessage.type === 'success' ? '#059669' : '#DC2626'
                  }}>
                    {profileMessage.text}
                  </div>
                )}
                
                <div style={{ marginTop: '2rem' }}>
                  <p>
                    Note: Les adresses de portefeuille sont maintenant gérées directement lors de la prise en charge des escrows,
                    pour faciliter l'utilisation de différentes adresses selon les crypto-monnaies.
                  </p>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Escrow Action Modal */}
      {escrowActionModal.isOpen && (
        <ModalOverlay>
          <ModalContent>
            <h3>
              {escrowActionModal.action === 'assign' && 'Prendre en charge cet escrow'}
              {escrowActionModal.action === 'complete' && 'Compléter cet escrow'}
              {escrowActionModal.action === 'reject' && 'Rejeter cet escrow'}
              {escrowActionModal.action === 'log' && 'Ajouter un log'}
              {escrowActionModal.action === 'sendFile' && 'Envoyer le fichier à l\'acheteur'}
            </h3>
            
            {escrowActionModal.action === 'reject' && (
              <FormGroup>
                <Label htmlFor="reason">Raison du rejet</Label>
                <Input
                  type="text"
                  id="reason"
                  placeholder="Entrez la raison du rejet"
                  value={reason}
                  onChange={handleEscrowReasonChange}
                />
              </FormGroup>
            )}

            {escrowActionModal.action === 'log' && (
              <FormGroup>
                <Label htmlFor="logMessage">Message à ajouter au log</Label>
                <Input
                  type="text"
                  id="logMessage"
                  placeholder="Entrez le message à ajouter au log"
                  value={logMessage}
                  onChange={handleLogMessageChange}
                />
              </FormGroup>
            )}

            {escrowActionModal.action === 'assign' && (
              <>
                <p>
                  En prenant en charge cet escrow, vous deviendrez responsable du suivi de la transaction. 
                  Votre adresse de portefeuille sera communiquée à l'acheteur pour le paiement.
                </p>
                
                {/* Champ obligatoire pour entrer l'adresse de portefeuille */}
                <FormGroup>
                  <Label htmlFor="tempWalletAddress">
                    <span style={{ color: '#e74c3c' }}>*</span> Adresse de portefeuille pour cet escrow:
                  </Label>
                  <Input
                    type="text"
                    id="tempWalletAddress"
                    placeholder="Entrez l'adresse à communiquer pour ce paiement"
                    value={tempWalletAddress}
                    onChange={handleTempWalletChange}
                    required
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      padding: '10px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      border: '1px solid #ddd'
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                    Cette adresse sera envoyée à l'acheteur via Telegram pour qu'il puisse effectuer le paiement.
                  </small>
                </FormGroup>
                
                {(() => {
                  // Trouver l'escrow correspondant
                  const currentEscrow = escrows.find(e => e._id === escrowActionModal.escrowId);
                  if (currentEscrow && currentEscrow.cryptoCurrency && currentEscrow.cryptoCurrency !== 'Non spécifiée') {
                    return (
                      <div style={{ 
                        backgroundColor: '#FEF3C7', 
                        borderRadius: '8px', 
                        padding: '1rem',
                        marginTop: '1rem',
                        marginBottom: '1rem',
                        border: '1px solid #FCD34D',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FaExclamationTriangle style={{ color: '#D97706' }} />
                        <span style={{ color: '#000' }}>
                          <strong>Crypto-monnaie demandée: {currentEscrow.cryptoCurrency}</strong>
                          <br />
                          Assurez-vous de fournir une adresse de portefeuille compatible avec cette crypto-monnaie.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            {escrowActionModal.action === 'complete' && (
              <p>
                En complétant cet escrow, vous confirmez que la transaction s'est bien déroulée.
                Les deux parties seront notifiées de la complétion.
              </p>
            )}

            {escrowActionModal.action === 'sendFile' && (
              <>
                <p>
                  Ce message sera envoyé à l'acheteur via Telegram avec les instructions pour accéder au fichier ou au lien.
                </p>
                <FormGroup>
                  <Label htmlFor="tempFileMessage">Message avec le lien/instructions:</Label>
                  <textarea
                    id="tempFileMessage"
                    placeholder="Entrez les instructions ou le lien pour l'acheteur"
                    value={tempFileMessage}
                    onChange={handleTempFileMessageChange}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                    Vous pouvez inclure le lien vers le fichier et toute instruction nécessaire pour l'acheteur.
                  </small>
                </FormGroup>
              </>
            )}

            <div style={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <Button 
                onClick={closeEscrowActionModal}
                variant="secondary"
              >
                Annuler
              </Button>
              <Button 
                onClick={submitEscrowAction}
                variant="primary"
                disabled={
                  (escrowActionModal.action === 'assign' && !tempWalletAddress.trim()) ||
                  (escrowActionModal.action === 'reject' && !reason) ||
                  (escrowActionModal.action === 'log' && !logMessage) ||
                  (escrowActionModal.action === 'sendFile' && !tempFileMessage.trim())
                }
              >
                Confirmer
              </Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* File Upload Modal */}
      {fileUploadModal.isOpen && (
        <ModalOverlay>
          <ModalContent>
            <h3>
              {fileUploadModal.contentType === 'file' && 'Uploader un fichier'}
              {fileUploadModal.contentType === 'link' && 'Envoi d\'un lien'}
              {fileUploadModal.contentType === 'text' && 'Envoi de texte'}
            </h3>
            
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginBottom: '1.5rem',
              flexWrap: 'wrap'
            }}>
              <Button 
                onClick={() => handleContentTypeChange('file')}
                variant={fileUploadModal.contentType === 'file' ? 'primary' : 'outline'}
                style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
              >
                <FaFile style={{ marginRight: '0.5rem' }} /> Fichier
              </Button>
              <Button 
                onClick={() => handleContentTypeChange('link')}
                variant={fileUploadModal.contentType === 'link' ? 'primary' : 'outline'}
                style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
              >
                <FaLink style={{ marginRight: '0.5rem' }} /> Lien
              </Button>
              <Button 
                onClick={() => handleContentTypeChange('text')}
                variant={fileUploadModal.contentType === 'text' ? 'primary' : 'outline'}
                style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem' }}
              >
                <FaAlignLeft style={{ marginRight: '0.5rem' }} /> Texte
              </Button>
            </div>
            
            {fileUploadModal.contentType === 'file' && (
              <FormGroup>
                <Label htmlFor="selectedFile">Fichier</Label>
                <Input
                  type="file"
                  id="selectedFile"
                  onChange={handleFileChange}
                />
              </FormGroup>
            )}

            {fileUploadModal.contentType === 'link' && (
              <FormGroup>
                <Label htmlFor="content">Lien</Label>
                <Input
                  type="text"
                  id="content"
                  placeholder="Entrez le lien"
                  value={content}
                  onChange={handleContentChange}
                />
              </FormGroup>
            )}

            {fileUploadModal.contentType === 'text' && (
              <FormGroup>
                <Label htmlFor="content">Texte</Label>
                <textarea
                  id="content"
                  placeholder="Entrez le texte"
                  value={content}
                  onChange={handleContentChange}
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
              </FormGroup>
            )}

            <div style={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <Button 
                onClick={closeFileUploadModal}
                variant="secondary"
              >
                Annuler
              </Button>
              <Button 
                onClick={submitContent}
                variant="primary"
                disabled={
                  (fileUploadModal.contentType === 'file' && !selectedFile) ||
                  (fileUploadModal.contentType === 'link' && !content.trim()) ||
                  (fileUploadModal.contentType === 'text' && !content.trim()) ||
                  loading.fileUpload
                }
              >
                {loading.fileUpload ? <LoadingIcon><FaSpinner className="spinner" /></LoadingIcon> : 'Confirmer'}
              </Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default AdminDashboard; 